import { Link } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";

const sections = [
  {
    title: "1. Wie is verantwoordelijk?",
    body: [
      "Bijen App is verantwoordelijk voor de verwerking van persoonsgegevens binnen de webapp en mobiele app. Voor vragen over privacy kun je contact opnemen via privacy@bijen.app.",
      "Deze privacyverklaring geldt voor iedereen die Bijen App gebruikt, een account aanmaakt, inlogt, gegevens vastlegt of contact met ons opneemt.",
    ],
  },
  {
    title: "2. Welke gegevens verwerken wij?",
    body: [
      "Accountgegevens: naam, e-mailadres, wachtwoordhash, verificatiestatus en inloggegevens die nodig zijn om je account veilig te laten werken.",
      "Imkerijgegevens: bijenstanden, kasten, inspecties, taken, planning, koninginnen, teeltgegevens, notities, foto's of bestanden die je zelf toevoegt en adviezen die op basis daarvan worden gemaakt.",
      "Technische gegevens: apparaat- en browserinformatie, sessietokens, mobiele tokens, foutmeldingen, IP-adres, beveiligingslogs en gegevens die nodig zijn voor synchronisatie, offline gebruik en QR- of scanfuncties.",
      "Locatiegegevens: alleen wanneer je die functie gebruikt, bijvoorbeeld om een bijenstand vast te leggen of je huidige locatie aan een actie te koppelen.",
      "Communicatiegegevens: berichten die je aan ons stuurt, bijvoorbeeld bij supportvragen of verzoeken over je gegevens.",
    ],
  },
  {
    title: "3. Waarvoor gebruiken wij deze gegevens?",
    body: [
      "Wij gebruiken persoonsgegevens om je account te maken en te beveiligen, de app te laten functioneren, je imkerijgegevens op te slaan en te synchroniseren, planning en adviezen te tonen, support te leveren en de betrouwbaarheid en veiligheid van Bijen App te verbeteren.",
      "Wij verkopen je persoonsgegevens niet. Wij gebruiken je gegevens ook niet voor advertentieprofielen van derden.",
    ],
  },
  {
    title: "4. Juridische grondslagen",
    body: [
      "Wij verwerken gegevens voor de uitvoering van de overeenkomst met jou, bijvoorbeeld wanneer je een account gebruikt en gegevens opslaat in Bijen App.",
      "Daarnaast verwerken wij gegevens wanneer dat nodig is voor ons gerechtvaardigd belang, zoals beveiliging, foutoplossing, misbruikpreventie en verbetering van de dienst.",
      "Voor functies die toestemming vragen, zoals locatie op je apparaat, verwerken wij die gegevens alleen wanneer je daar toestemming voor geeft. Je kunt die toestemming via je apparaatinstellingen weer intrekken.",
    ],
  },
  {
    title: "5. Delen met derden",
    body: [
      "Wij delen persoonsgegevens alleen wanneer dat nodig is om Bijen App te leveren, bijvoorbeeld met hosting-, database-, e-mail-, analyse- of foutmonitoringdiensten die namens ons werken.",
      "Met verwerkers maken wij afspraken over beveiliging en vertrouwelijkheid. Wij delen gegevens ook wanneer dit wettelijk verplicht is of nodig is om onze rechten, gebruikers of systemen te beschermen.",
    ],
  },
  {
    title: "6. Bewaartermijnen",
    body: [
      "Wij bewaren account- en appgegevens zolang je account actief is of zolang dit nodig is om Bijen App te leveren.",
      "Wanneer je gegevens verwijdert of je account laat verwijderen, verwijderen of anonimiseren wij de gekoppelde gegevens, behalve wanneer wij bepaalde gegevens langer moeten bewaren vanwege wettelijke verplichtingen, beveiliging, geschilafhandeling of technische back-ups.",
      "Back-ups worden volgens ons normale back-upbeleid overschreven. Daardoor kan het kort duren voordat verwijderde gegevens volledig uit back-ups verdwenen zijn.",
    ],
  },
  {
    title: "7. Beveiliging",
    body: [
      "Wij nemen passende technische en organisatorische maatregelen om je gegevens te beschermen, waaronder versleutelde verbindingen, toegangsbeperking, wachtwoordhashing, sessiebeveiliging en logging voor misbruikpreventie.",
      "Geen enkel systeem is volledig risicovrij. Gebruik daarom een sterk wachtwoord en deel je inloggegevens niet met anderen.",
    ],
  },
  {
    title: "8. Jouw rechten",
    body: [
      "Je hebt het recht om je persoonsgegevens in te zien, te corrigeren, te verwijderen, over te dragen, verwerking te beperken of bezwaar te maken tegen bepaalde verwerkingen.",
      "Je kunt ook vragen om je account en gegevens te verwijderen via de pagina Account en gegevens verwijderen. Voor privacyverzoeken kun je mailen naar privacy@bijen.app. Wij kunnen vragen om informatie waarmee we kunnen controleren dat het verzoek echt van jou komt.",
      "Ben je niet tevreden over hoe wij met je verzoek omgaan, dan kun je een klacht indienen bij de Autoriteit Persoonsgegevens.",
    ],
  },
  {
    title: "9. Cookies en lokale opslag",
    body: [
      "Bijen App gebruikt cookies en lokale opslag die nodig zijn voor inloggen, sessiebeheer, beveiliging, voorkeuren, offline gebruik en synchronisatie.",
      "Wanneer wij analytische of vergelijkbare technieken gebruiken, doen wij dat om de dienst te verbeteren en zo privacyvriendelijk mogelijk.",
    ],
  },
  {
    title: "10. Wijzigingen",
    body: [
      "Wij kunnen deze privacyverklaring aanpassen wanneer Bijen App, wetgeving of onze werkwijze verandert. De nieuwste versie staat altijd op deze pagina.",
    ],
  },
];

export default function Privacy() {
  return (
    <main className="min-h-screen bg-zinc-50 text-zinc-950">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <header className="mb-8 rounded-2xl border border-zinc-200 bg-white p-5 sm:p-7">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-4">
              <Link to="/" className="inline-flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-600 text-xl text-white" aria-hidden="true">🐝</span>
                <span>
                  <span className="block font-display text-2xl font-bold tracking-tight">Bijen App</span>
                  <span className="block text-sm text-zinc-500">Bijenkastbeheer</span>
                </span>
              </Link>
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-800">
                  <ShieldCheck className="h-4 w-4" />
                  Privacy
                </div>
                <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Privacyverklaring</h1>
                <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
                  In deze verklaring leggen we helder uit welke persoonsgegevens Bijen App verwerkt, waarom we dat doen
                  en welke keuzes en rechten je hebt.
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600 sm:w-56">
              <div className="font-semibold text-zinc-950">Laatst bijgewerkt</div>
              <div>13 mei 2026</div>
            </div>
          </div>
        </header>

        <section className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
          <div className="font-bold">Kort samengevat</div>
          <p className="mt-1">
            Bijen App verwerkt je gegevens om de app te laten werken: accounts, bijenstanden, kasten, inspecties,
            planning, synchronisatie en beveiliging. We verkopen je gegevens niet en delen ze alleen met partijen die
            nodig zijn om de dienst te leveren of wanneer de wet dat vereist.
          </p>
        </section>

        <div className="space-y-4">
          {sections.map((section) => (
            <section key={section.title} className="rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6">
              <h2 className="font-display text-xl font-bold tracking-tight">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-6 text-zinc-600">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-6 rounded-2xl border border-zinc-200 bg-white p-5 text-sm leading-6 text-zinc-600">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-bold text-zinc-950">Vragen over privacy?</div>
              <div>Stuur ons een bericht en vermeld waar je verzoek over gaat.</div>
            </div>
            <a
              href="mailto:privacy@bijen.app"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 font-bold text-white shadow-sm shadow-amber-600/20 hover:bg-amber-700"
            >
              <Mail className="h-4 w-4" />
              privacy@bijen.app
            </a>
          </div>
        </footer>
      </div>
    </main>
  );
}
